import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AuthService } from '../../core/services/auth/auth.service';
import { PopupService } from '../../core/services/popup/popup.service';
import { MessagePopupComponent } from '../../shared/components/message-popup/message-popup.component';

import { v4 as uuidv4 } from "uuid";

type AskResponse = { answer: string; sessionId?: string; [k: string]: any };

@Injectable({ providedIn: 'root' })
export class ChatbotService {
  private readonly apiUrl = environment.do_services_sb_url + 'chatbot/ask';
  private sessionId: string | null = null;

  constructor(
    private readonly http: HttpClient,
    private readonly authService: AuthService,
    private readonly popupService: PopupService,
    private readonly router: Router) { }

  private withTimeout<T>(p: Promise<T>, ms = 30000): Promise<T> {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort('timeout'), ms);
    return Promise.race([
      p.finally(() => clearTimeout(t)),
      new Promise<T>((_, rej) => {
        ctrl.signal.addEventListener('abort', () => rej(new Error('Request timed out')));
      }),
    ]);
  }

  async ask(query: string, timeoutMs = 30000): Promise<AskResponse> {
    if(!query?.trim()) throw new Error('Empty query');

    const res = await this.fetchChatbotResponse(query, timeoutMs);

    if(res['error']) {
      throw new Error('chatbot_error');
    }
    return res;
  }

  private async fetchChatbotResponse(query: string, timeoutMs: number): Promise<AskResponse> {
    const requestBody: any = { query };
    
    if(this.sessionId) {
      requestBody.sessionId = this.sessionId;
    } else {
      this.sessionId = uuidv4();
      requestBody.sessionId = this.sessionId;
    }

    try {
      const response$ = this.http.post<AskResponse>(this.apiUrl, requestBody);
      const response = await this.withTimeout(firstValueFrom(response$), timeoutMs);
      return response;
    } catch (error: any) {
      if(error?.status === 401) {
        const dialogRef = this.popupService.openDialog(MessagePopupComponent, {
          data: { message: 'Unauthorized Request - Your Session has expired' }
        });
        dialogRef.afterClosed().subscribe(() => {
          this.authService.logout().subscribe(() => {
            this.router.navigate(['/home']);
          });
        });
        return { answer: '', error: 'session_expired' };
      }
      return { 
        answer: '', 
        error: error?.message || 'Unknown error occurred' 
      };
    }
  }
}